// File: src/DVACheckPage.js
// OOOSH Driver Verification — the Northern Ireland branch of the licence check
//
// WHY THIS PAGE EXISTS
// --------------------
// A GB driver does their own licence check: they generate a share code at
// viewdrivingrecord.service.gov.uk, download the summary and upload it here, and
// document-processor.js reads it. A Northern Ireland driver cannot. That service
// holds GB licences only — a DVA licence simply isn't in it.
//
// The driver is still a UK driver and still needs the check: OP's router
// (`isUkLicence` in services/driver-validity.ts) correctly sends a DVA licence
// to `dvla-check`, because NI is the UK. What was wrong was this step's page,
// which hardcoded the gov.uk link. So all six of OP's router branches led back
// to `dvla-check`, and `dvla-check` led to a service that would never hold the
// driver's licence. A dead end with no error message and no way out
// (Declan Haughian / 16286, Sep 2026 — everything else done, parked forever).
//
// WHY IT IS NOT A NEW ROUTER STEP
// -------------------------------
// `dvla-check` is already the right destination and OP already routes here
// correctly, so nothing in the routing engine changes — and crucially nothing
// needs adding to ProcessingHub's two `stepMapping[nextStep] || 'poa1'` maps or
// to `isValidStepTransition`. A new step that misses either of those silently
// dumps the driver on a proof-of-address page. App.js picks this component over
// DVLAPreviewPage for the same step, on `isNiIssuer`.
//
// WHAT THE DRIVER CAN ACTUALLY DO
// -------------------------------
// DVA's equivalent service is at nidirect: the driver creates a single-use
// check code and a THIRD PARTY runs the lookup. So the most this page can
// collect is the code, and then it must stop — a member of staff does the rest.
// That makes this a terminal screen by design, which is only acceptable because
// recording the code fires OP's staff alert (email + bell). A parked driver
// nobody was told about is the spinning-wheel-of-death pattern (ProcessingHub,
// Jul 2026).
//
// The driver comes back to sign once staff have recorded the check — by then
// OP's router sends them to `signature`, so the hire form link in their email
// works unchanged.

import React, { useState } from 'react';
import reportError from './utils/errorReporter';
import StuckDriverHelper from './StuckDriverHelper';
import { StepShell, Card, PrimaryButton, Note, Why, PHASE } from './ui/HireFormUI';

const NIDIRECT_SHARE_URL =
  'https://www.nidirect.gov.uk/services/view-or-share-your-ni-driving-licence-information';

const DVACheckPage = ({
  email,
  jobId,
  driverName,
  licenceNumber,
  existingCheckCode,
  onBack
}) => {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [failureCount, setFailureCount] = useState(0);
  // Seeded from what OP already holds, so a driver who reloads or comes back
  // later sees "we're on it" rather than being asked for the code again. The
  // page is self-healing that way: OP keeps routing them to `dvla-check` until
  // staff record the check, and this is what makes that landing sane.
  const [submittedCode, setSubmittedCode] = useState(existingCheckCode || '');
  const [changing, setChanging] = useState(false);

  const handleSubmit = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Please enter the check code from nidirect.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/.netlify/functions/dva-check-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, checkCode: trimmed, jobId })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${response.status})`);
      }

      setSubmittedCode(trimmed);
      setChanging(false);
      setCode('');
    } catch (err) {
      console.error('❌ Failed to record DVA check code:', err);
      setFailureCount((n) => n + 1);
      setError('We could not save that code. Please try again.');
      // Critical, not a warning: if this never lands, nobody is told the driver
      // is waiting and they sit on a screen they cannot leave.
      reportError({
        page: 'DVACheckPage',
        error: err.message,
        severity: 'critical',
        email,
        additionalInfo: { jobId }
      });
    } finally {
      setSubmitting(false);
    }
  };

  // App passes jobId; the URL is the fallback so the header still names the job.
  const shellJobId = jobId || new URLSearchParams(window.location.search).get('job');

  // ── Parked: we have a code, a human is doing the lookup ───────────────────
  // A terminal waiting screen: the driver has nothing to do until staff have
  // run the check. No StuckDriverHelper here on purpose — its 5-minute
  // "Having trouble?" would pop up on a screen where nothing is wrong.
  if (submittedCode && !changing) {
    return (
      <StepShell jobId={shellJobId} phase={PHASE.SIGN}>
        <Card>
          <h2 className="hf-h2">We&rsquo;re checking your licence</h2>
          <Note tone="green">
            <strong>Nothing more to do right now.</strong> We&rsquo;ve got your check code and our team
            has been told.
          </Note>

          <div className="hf-inset">
            <p className="hf-small">Your check code</p>
            <p className="hf-code" style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 600, color: 'var(--hf-ink)' }}>
              {submittedCode}
            </p>
          </div>

          <p className="hf-body">
            Northern Ireland licences aren&rsquo;t on the GOV.UK check service, so we run this check
            ourselves with the DVA.
          </p>

          <Why lead="What happens next:">
            we&rsquo;ll email you as soon as it&rsquo;s done, and the link in that email will take you
            straight to signing your hire agreement. You can close this page.
          </Why>

          <div className="hf-stack">
            <button
              type="button"
              onClick={() => { setChanging(true); setError(''); }}
              className="hf-btn-secondary"
            >
              Send a different code
            </button>
            {/* The code is single use and expires 21 days after they made it, so
                "send a different one" is a real need, not an edge case. */}
            <p className="hf-small hf-center">
              Only if we&rsquo;ve asked you for a new one. Each code can only be used once.
            </p>
          </div>
        </Card>
      </StepShell>
    );
  }

  // ── Collecting the code ───────────────────────────────────────────────────
  return (
    <StepShell jobId={shellJobId} phase={PHASE.SIGN}>
      <Card>
        <h2 className="hf-h2">Your driving record</h2>
        <p className="hf-intro">
          Your licence was issued by the DVA in Northern Ireland, so it isn&rsquo;t on the GOV.UK
          driving record service. Northern Ireland has its own: you create a check code there, and
          we do the rest.
        </p>

        <ol className="hf-numlist">
          <li>
            <span className="hf-dot">1</span>
            <span>
              Go to{' '}
              <a className="hf-link" href={NIDIRECT_SHARE_URL} target="_blank" rel="noopener noreferrer">
                view or share your driving licence
              </a>{' '}
              on nidirect.
            </span>
          </li>
          <li><span className="hf-dot">2</span><span>Sign in. You&rsquo;ll need a verified nidirect account.</span></li>
          <li><span className="hf-dot">3</span><span>Choose <strong>share your licence</strong> and create a check code.</span></li>
          <li><span className="hf-dot">4</span><span>Come back to this tab and enter it below.</span></li>
        </ol>

        <div className="hf-field">
          <label className="hf-label" htmlFor="dva-check-code">Your check code</label>
          {/*
            NOT upper-cased, and autoCapitalize off. A DVA code is mixed case
            ("BML1s646") and the lookup is case-sensitive, so "helpfully"
            normalising it on a phone keyboard would hand our team a code that
            doesn't work and send the driver back for another one. No CSS
            text-transform either, for the same reason.
          */}
          <input
            id="dva-check-code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. BML1s646"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            disabled={submitting}
            className="hf-input hf-code"
          />
          <p className="hf-help-text">
            Copy it exactly as shown. Capital letters matter.
          </p>
        </div>

        {licenceNumber && (
          <p className="hf-small">
            We&rsquo;ll use it with your licence number ending{' '}
            <strong className="hf-code">{String(licenceNumber).slice(-8)}</strong>.
          </p>
        )}

        {error && <Note tone="error">{error}</Note>}

        <Why lead="What happens next:">
          our team runs the check with the DVA, then emails you a link to sign your hire agreement.
        </Why>

        <div className="hf-stack">
          <PrimaryButton
            onClick={handleSubmit}
            disabled={!code.trim()}
            busy={submitting}
            busyLabel="Sending…"
          >
            {code.trim() ? 'Send my check code' : 'Enter your check code to continue'}
          </PrimaryButton>

          {onBack && (
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              className="hf-btn-secondary"
            >
              Back
            </button>
          )}
        </div>

        <StuckDriverHelper failureCount={failureCount} pageName="DVACheckPage" email={email} />
      </Card>
    </StepShell>
  );
};

export default DVACheckPage;
